// Client-side Palworld card price alerts.
//
// Alerts are deliberately device-local (localStorage): they need no account, fire
// instantly on the board and survive reloads. Each alert stores a USD target so
// the trigger is currency-independent even when the board is displayed in EUR,
// GBP or JPY.

export interface PriceAlert {
  code: string;
  name: string;
  direction: "above" | "below";
  /** Target in USD. */
  target: number;
  createdAt: string;
  /** Market value when the alert was created, for context in the list. */
  createdAtValue: number;
  /** ISO time the alert last fired, so we can show "triggered" state. */
  triggeredAt?: string;
}

const KEY = "palstars:price-alerts";

export function readAlerts(): PriceAlert[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as PriceAlert[];
    return Array.isArray(parsed) ? parsed.filter((a) => a && typeof a.code === "string") : [];
  } catch {
    return [];
  }
}

export function writeAlerts(alerts: PriceAlert[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(alerts.slice(0, 60)));
  } catch {
    /* storage full or blocked — alerts are a convenience, never fatal */
  }
}

export function alertKey(a: Pick<PriceAlert, "code" | "direction" | "target">): string {
  return `${a.code}:${a.direction}:${a.target}`;
}

export function isTriggered(alert: PriceAlert, market: number): boolean {
  return alert.direction === "above" ? market >= alert.target : market <= alert.target;
}
